
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Download,
  Eye,
  FileClock,
  IdCard,
  LayoutTemplate,
  Loader2,
  RefreshCw,
  RotateCcw,
  Search,
  ShieldX,
} from 'lucide-react';
import { useSchool } from '../../../context/SchoolContext';
import { idCardApi } from '../services/idCardApi';
import {
  documentTypeForOwner,
  downloadBlobResponse,
  formatDate,
  formatDateTime,
  getApiErrorMessage,
  humanize,
  unwrapApiResponse,
} from '../utils/documentUtils';
import {
  EmptyState,
  ErrorBanner,
  LoadingBlock,
  Modal,
  PageHeader,
  Pagination,
  StatusBadge,
} from '../components/DocumentUi';

const PAGE_SIZE = 20;
const CARD_STATUSES = ['GENERATING', 'ACTIVE', 'EXPIRED', 'REVOKED', 'REPLACED', 'FAILED'];

const CardHistoryPage = () => {
  const navigate = useNavigate();
  const { schoolId, activeAcademicYear, activeAcademicYearId } = useSchool();
  const [cards, setCards] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ q: '', owner_type: '', academic_year_id: activeAcademicYearId || '', status: '' });
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [detailCard, setDetailCard] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState('');
  const [action, setAction] = useState(null);
  const [reason, setReason] = useState('');
  const [reissueTemplateId, setReissueTemplateId] = useState('');
  const [reissueTemplates, setReissueTemplates] = useState([]);
  const [actionSaving, setActionSaving] = useState(false);

  const loadCards = useCallback(async (silent = false) => {
    if (!schoolId) return;
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError('');
    try {
      const response = await idCardApi.getCardHistory(schoolId, {
        ...filters,
        page,
        page_size: PAGE_SIZE,
      });
      const result = unwrapApiResponse(response);
      setCards(result.data || []);
      setPagination(result.pagination);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Failed to load ID-card history.'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters, page, schoolId]);

  useEffect(() => {
    setFilters((previous) => ({
      ...previous,
      academic_year_id: activeAcademicYearId || '',
    }));
    setPage(1);
  }, [activeAcademicYearId]);

  useEffect(() => {
    loadCards();
  }, [loadCards]);

  const updateFilter = (key, value) => {
    setFilters((previous) => ({ ...previous, [key]: value }));
    setPage(1);
  };

  const openDetail = async (card) => {
    setDetailCard(card);
    setDetailLoading(true);
    setError('');
    try {
      const response = await idCardApi.getCard(schoolId, card.id);
      setDetailCard(unwrapApiResponse(response).data || card);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Failed to load ID-card details.'));
    } finally {
      setDetailLoading(false);
    }
  };

  const downloadCard = async (card) => {
    setDownloadingId(card.id);
    setError('');
    try {
      const response = await idCardApi.downloadCard(schoolId, card.id);
      downloadBlobResponse(response, `${card.card_number || card.id}.pdf`);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Could not download this ID card.'));
    } finally {
      setDownloadingId('');
    }
  };

  const openAction = async (mode, card) => {
    setAction({ mode, card });
    setReason('');
    setReissueTemplateId('');
    setReissueTemplates([]);
    if (mode === 'REISSUE') {
      try {
        const response = await idCardApi.getTemplates(
          schoolId,
          documentTypeForOwner(card.owner_type),
        );
        setReissueTemplates(unwrapApiResponse(response).data || []);
      } catch (requestError) {
        setError(getApiErrorMessage(requestError, 'Failed to load replacement templates.'));
      }
    }
  };

  const submitAction = async () => {
    if (!action || !reason.trim()) return;
    setActionSaving(true);
    setError('');
    try {
      if (action.mode === 'REISSUE') {
        const response = await idCardApi.reissueCard(schoolId, action.card.id, {
          reason: reason.trim(),
          ...(reissueTemplateId ? { template_id: reissueTemplateId } : {}),
        });
        const job = unwrapApiResponse(response).data;
        setAction(null);
        setDetailCard(null);
        navigate('/documents/generation-jobs', { state: { createdJobId: job?.id } });
      } else {
        await idCardApi.revokeCard(schoolId, action.card.id, { reason: reason.trim() });
        setAction(null);
        setDetailCard(null);
        await loadCards(true);
      }
    } catch (requestError) {
      setError(
        getApiErrorMessage(
          requestError,
          action.mode === 'REISSUE' ? 'Could not create a reissue job.' : 'Could not revoke this ID card.',
        ),
      );
    } finally {
      setActionSaving(false);
    }
  };

  const activeCount = useMemo(
    () => cards.filter((card) => card.status === 'ACTIVE').length,
    [cards],
  );

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <PageHeader
        title="Card History"
        description="Search issued student and staff cards, download historical PDFs, and manage reissue or revocation without overwriting prior versions."
        actions={
          <>
            <Link to="/documents/id-cards" className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"><IdCard className="h-4 w-4" /> Generate</Link>
            <Link to="/documents/templates" className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"><LayoutTemplate className="h-4 w-4" /> Templates</Link>
            <Link to="/documents/generation-jobs" className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"><FileClock className="h-4 w-4" /> Jobs</Link>
          </>
        }
      />

      <ErrorBanner message={error} onDismiss={() => setError('')} />

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"><p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Returned cards</p><p className="mt-2 text-2xl font-bold text-gray-900">{cards.length}</p></div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"><p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Active on this page</p><p className="mt-2 text-2xl font-bold text-emerald-700">{activeCount}</p></div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"><p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Total matching</p><p className="mt-2 text-2xl font-bold text-gray-900">{pagination?.count ?? cards.length}</p></div>
      </div>

      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <label className="relative xl:col-span-2"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" /><input value={filters.q} onChange={(event) => updateFilter('q', event.target.value)} placeholder="Search card number or document title" className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm" /></label>
          <select value={filters.owner_type} onChange={(event) => updateFilter('owner_type', event.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm"><option value="">Student and staff</option><option value="STUDENT">Students</option><option value="STAFF">Staff</option></select>
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-blue-500">Working Academic Year</p>
            <p className="text-sm font-semibold text-blue-800">{activeAcademicYear?.year_name || 'No academic year selected'}</p>
          </div>
          <select value={filters.status} onChange={(event) => updateFilter('status', event.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm"><option value="">All statuses</option>{CARD_STATUSES.map((status) => <option key={status} value={status}>{humanize(status)}</option>)}</select>
        </div>
        <div className="mt-3 flex justify-end"><button type="button" disabled={refreshing} onClick={() => loadCards(true)} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"><RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh</button></div>
      </section>

      <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {loading ? (
          <LoadingBlock label="Loading ID-card history..." />
        ) : cards.length === 0 ? (
          <EmptyState title="No card history found" description="Generate an ID card or change the history filters." />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"><tr><th className="px-5 py-3">Card number</th><th className="px-5 py-3">Owner</th><th className="px-5 py-3">Template / version</th><th className="px-5 py-3">Validity</th><th className="px-5 py-3">Status</th><th className="px-5 py-3">Issued</th><th className="px-5 py-3 text-right">Actions</th></tr></thead>
              <tbody className="divide-y divide-gray-100">
                {cards.map((card) => (
                  <tr key={card.id} className="hover:bg-gray-50">
                    <td className="px-5 py-4"><p className="font-mono text-xs font-semibold text-gray-900">{card.card_number}</p><p className="mt-1 text-xs text-gray-500">{card.academic_year_name || 'No academic year'}</p></td>
                    <td className="px-5 py-4"><p className="font-semibold text-gray-900">{card.owner_name || 'Unnamed record'}</p><p className="mt-1 text-xs text-gray-500">{humanize(card.owner_type)}</p></td>
                    <td className="px-5 py-4"><p className="font-medium text-gray-700">{card.template_name || '—'}</p><p className="mt-1 text-xs text-gray-500">Version {card.version}</p></td>
                    <td className="px-5 py-4 text-gray-600"><p>{formatDate(card.valid_from)}</p><p className="text-xs text-gray-400">to {formatDate(card.valid_until)}</p></td>
                    <td className="px-5 py-4"><StatusBadge value={card.status} /></td>
                    <td className="px-5 py-4 text-gray-600">{formatDateTime(card.issued_at || card.created_at)}</td>
                    <td className="px-5 py-4"><div className="flex justify-end gap-2"><button type="button" onClick={() => openDetail(card)} className="rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-50" title="View"><Eye className="h-4 w-4" /></button><button type="button" disabled={downloadingId === card.id || !card.generated_document} onClick={() => downloadCard(card)} className="rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-50 disabled:opacity-40" title="Download">{downloadingId === card.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}</button>{card.status === 'ACTIVE' && <><button type="button" onClick={() => openAction('REISSUE', card)} className="rounded-lg border border-blue-200 p-2 text-blue-600 hover:bg-blue-50" title="Reissue"><RotateCcw className="h-4 w-4" /></button><button type="button" onClick={() => openAction('REVOKE', card)} className="rounded-lg border border-red-200 p-2 text-red-600 hover:bg-red-50" title="Revoke"><ShieldX className="h-4 w-4" /></button></>}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination pagination={pagination} page={page} onPageChange={setPage} disabled={loading} />
      </section>

      <Modal
        open={Boolean(detailCard)}
        onClose={() => setDetailCard(null)}
        title={detailCard?.card_number || 'ID-card detail'}
        description="Immutable card version and generated-document information."
        size="max-w-3xl"
        footer={detailCard ? <div className="flex flex-wrap justify-end gap-2"><button type="button" disabled={!detailCard.generated_document || downloadingId === detailCard.id} onClick={() => downloadCard(detailCard)} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-white disabled:opacity-40"><Download className="h-4 w-4" /> Download PDF</button>{detailCard.status === 'ACTIVE' && <><button type="button" onClick={() => openAction('REISSUE', detailCard)} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"><RotateCcw className="h-4 w-4" /> Reissue</button><button type="button" onClick={() => openAction('REVOKE', detailCard)} className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"><ShieldX className="h-4 w-4" /> Revoke</button></>}</div> : null}
      >
        {detailLoading ? <LoadingBlock label="Loading card detail..." /> : detailCard ? (
          <div className="space-y-5">
            <div className="flex items-start justify-between rounded-xl bg-gray-50 p-4"><div><p className="text-sm text-gray-500">Owner</p><p className="mt-1 text-lg font-bold text-gray-900">{detailCard.owner_name || 'Unnamed record'}</p><p className="text-sm text-gray-500">{humanize(detailCard.owner_type)} · {detailCard.academic_year_name || 'No academic year'}</p></div><StatusBadge value={detailCard.status} /></div>
            <dl className="grid gap-4 md:grid-cols-2">
              {[['Template', detailCard.template_name], ['Version', detailCard.version], ['Issued at', formatDateTime(detailCard.issued_at)], ['Valid from', formatDate(detailCard.valid_from)], ['Valid until', formatDate(detailCard.valid_until)], ['Generation job', detailCard.generation_job], ['Replaced card', detailCard.replaced_card], ['Revoked at', formatDateTime(detailCard.revoked_at)]].map(([label, value]) => <div key={label} className="rounded-lg border border-gray-200 p-3"><dt className="text-xs font-semibold uppercase text-gray-500">{label}</dt><dd className="mt-1 break-all text-sm font-medium text-gray-900">{value || '—'}</dd></div>)}
            </dl>
            {detailCard.revocation_reason && <div className="rounded-xl border border-red-200 bg-red-50 p-4"><p className="text-xs font-semibold uppercase text-red-600">Revocation reason</p><p className="mt-1 text-sm text-red-800">{detailCard.revocation_reason}</p></div>}
            {detailCard.generated_document && <div className="rounded-xl border border-gray-200 p-4"><p className="font-semibold text-gray-900">Generated document</p><p className="mt-1 text-sm text-gray-600">{detailCard.generated_document.title || detailCard.generated_document.document_no}</p><p className="mt-1 text-xs text-gray-500">{detailCard.generated_document.id}</p></div>}
          </div>
        ) : null}
      </Modal>

      <Modal
        open={Boolean(action)}
        onClose={() => !actionSaving && setAction(null)}
        title={action?.mode === 'REISSUE' ? 'Reissue ID card' : 'Revoke ID card'}
        description={action?.mode === 'REISSUE' ? 'Creates a new immutable card version and marks the old card replaced after generation.' : 'Immediately invalidates the active card and its public verification result.'}
        size="max-w-lg"
        footer={<div className="flex justify-end gap-2"><button type="button" disabled={actionSaving} onClick={() => setAction(null)} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700">Cancel</button><button type="button" disabled={actionSaving || !reason.trim()} onClick={submitAction} className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-bold text-white disabled:opacity-50 ${action?.mode === 'REISSUE' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'}`}>{actionSaving && <Loader2 className="h-4 w-4 animate-spin" />}{action?.mode === 'REISSUE' ? 'Create reissue job' : 'Confirm revocation'}</button></div>}
      >
        <div className="space-y-4">
          <div className="rounded-lg bg-gray-50 p-3 text-sm"><p className="font-semibold text-gray-900">{action?.card?.owner_name}</p><p className="mt-1 font-mono text-xs text-gray-500">{action?.card?.card_number}</p></div>
          {action?.mode === 'REISSUE' && <label className="block text-sm font-medium text-gray-700">Replacement template<select value={reissueTemplateId} onChange={(event) => setReissueTemplateId(event.target.value)} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"><option value="">Keep current template</option>{reissueTemplates.map((template) => <option key={template.id} value={template.id}>{template.name}</option>)}</select></label>}
          <label className="block text-sm font-medium text-gray-700">Reason<textarea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={500} rows={4} placeholder={action?.mode === 'REISSUE' ? 'For example: photograph updated' : 'For example: card reported lost'} className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-normal" /><span className="mt-1 block text-right text-xs text-gray-400">{reason.length}/500</span></label>
        </div>
      </Modal>
    </div>
  );
};

export default CardHistoryPage;
